import qs from 'qs';

import type {
  AttractionImageMutationResponse,
  GetAttractionResponse,
  GetAttractionStatsParams,
  GetAttractionStatsResponse,
  GetFilterOptionsResponse,
  ListAttractionCitiesResponse,
  ListAttractionImagesResponse,
  ListAttractionOffersParams,
  ListAttractionOffersResponse,
  ListAttractionsParams,
  ListAttractionsResponse,
} from '~/types/attractionTypes';

import { json_headers, request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

type GetAttractionOptions = RequestOptions & {
  v?: string; // CDN cache-buster query param to bypass CloudFront caching
};

const BASE_PATH = `${window.configs.API_HOST}/api/experiences/attractions`;

/**
 * Lists attractions (admin endpoint, includes inactive).
 */
export async function listAttractions(
  params: ListAttractionsParams,
  options: RequestOptions = {},
): Promise<ListAttractionsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/admin?${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function getAttraction(
  attractionId: string,
  options: GetAttractionOptions = {},
): Promise<GetAttractionResponse> {
  const { v, ...requestOptions } = options;
  const query = v ? `?${qs.stringify({ v })}` : '';
  return request(`${BASE_PATH}/${attractionId}${query}`, {
    ...requestOptions,
    method: 'GET',
  });
}

export async function getAttractionCities(options: RequestOptions = {}): Promise<ListAttractionCitiesResponse> {
  return request(`${BASE_PATH}/cities`, {
    ...options,
    method: 'GET',
  });
}

export async function getAttractionFilterOptions(
  params: { countryCode?: string; state?: string; province?: string },
  options: RequestOptions = {},
): Promise<GetFilterOptionsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/filter-options?${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function getAttractionStats(
  params: GetAttractionStatsParams,
  options: RequestOptions = {},
): Promise<GetAttractionStatsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/stats?${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function getAttractionOffers(
  attractionId: string,
  params: ListAttractionOffersParams,
  options: RequestOptions = {},
): Promise<ListAttractionOffersResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/${attractionId}/offers?${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function updateAttractionActive(
  attractionId: string,
  active: boolean,
  options: RequestOptions = {},
): Promise<void> {
  return request(`${BASE_PATH}/${attractionId}/active`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ active }),
    headers: json_headers,
  });
}

export async function getAttractionImages(
  attractionId: string,
  options: RequestOptions = {},
): Promise<ListAttractionImagesResponse> {
  return request(`${BASE_PATH}/${attractionId}/images`, {
    ...options,
    method: 'GET',
  });
}

export async function updateAttractionImage(
  attractionId: string,
  imageId: string,
  imageUrl: string,
  options: RequestOptions = {},
): Promise<AttractionImageMutationResponse> {
  const response: { result: AttractionImageMutationResponse } = await request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ imageId, imageUrl }),
    headers: json_headers,
  });
  return response.result;
}

export async function resetAttractionImage(
  attractionId: string,
  options: RequestOptions = {},
): Promise<AttractionImageMutationResponse> {
  const response: { result: AttractionImageMutationResponse } = await request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'DELETE',
  });
  return response.result;
}
